import { ICellRendererParams } from "ag-grid-community";

import { AuthUserContextType } from "../../context/AuthUser";
import {
  QuotationDetailFormRowValues,
  QuotationDetailFormValues,
} from "../../model/form/QuotationDetailForm";
import {
  OrderFactor,
  ProductCodeConst,
  QuotationStatus,
} from "../../util/Constant";

/** 表示・編集可否判定に必要な画面状態 */
type QuotationDetailPermissionContext = {
  inpQuotation: QuotationDetailFormValues;
  inpDetails: QuotationDetailFormRowValues[];
  readOnly: boolean;
  auth: AuthUserContextType;
};

export type QuotationDetailPermission = ReturnType<
  typeof createQuotationDetailPermission
>;

/**
 * 見積詳細の表示・編集可否判定
 * （ステータス × ユーザ種別 × 見積種別 による判定を一箇所に集約）
 * 毎レンダリングで呼び出し、その時点の state に基づく判定関数群を返す。
 * 判定関数は state を更新せず、引数で受け取った値だけを参照する。
 */
export function createQuotationDetailPermission(
  ctx: QuotationDetailPermissionContext
) {
  const { inpQuotation, inpDetails, readOnly, auth } = ctx;

  // 行追加可否
  function canAddNewRow() {
    if (readOnly) {
      // 読み取り専用モードの時は不可
      return false;
    }

    //顧客かつLCM経由の場合は不可
    if (
      !auth.user().bemacFlg &&
      OrderFactor.LCM_PARTS === inpQuotation.quotationClassValue
    ) {
      return false;
    }

    // 「作成中」「依頼中」は可
    return [QuotationStatus.DRAFT, QuotationStatus.REQUESTED].includes(
      inpQuotation.statusClassValue
    );
  }

  // BEMAC回答列表示条件
  function showsAnswerColumns() {
    if (
      !auth.user().bemacFlg &&
      (inpQuotation.statusClassValue === QuotationStatus.DRAFT ||
        inpQuotation.statusClassValue === QuotationStatus.REQUESTED)
    ) {
      return false;
    }
    return true;
  }

  function isLcm() {
    return inpQuotation.quotationClassValue === OrderFactor.LCM_PARTS;
  }

  // 技師一般ユーザかどうか
  function isGeneralEngineer() {
    return auth.isEngineer() && !auth.user().adminFlg;
  }

  // 添付ファイル使用可否判定
  function editableFileUpload(isBemacArea: boolean) {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }
    if (auth.user().bemacFlg !== isBemacArea) {
      // BEMAC・顧客それぞれ自身のエリアのみ編集可能
      return false;
    }
    // ステータスが「確認中」の時は編集不可
    if (inpQuotation.statusClassValue === QuotationStatus.ANSWERED) {
      return false;
    }

    // その他の場合編集可能
    return true;
  }

  // コメント編集可否判定
  function editableComment(isBemacArea: boolean) {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }
    if (auth.user().bemacFlg !== isBemacArea) {
      // BEMAC・顧客それぞれ自身のエリアのみ編集可能
      return false;
    }

    if (auth.user().bemacFlg) {
      // BEMAC：作成中、依頼中は編集可能
      switch (inpQuotation.statusClassValue) {
        case QuotationStatus.DRAFT:
        case QuotationStatus.REQUESTED:
          return true;
      }
    } else {
      // 顧客：作成中、依頼中、回答確認中は編集可能
      switch (inpQuotation.statusClassValue) {
        case QuotationStatus.DRAFT:
        case QuotationStatus.REQUESTED:
        case QuotationStatus.ANSWERED:
          return true;
      }
    }

    // その他の場合編集不可
    return false;
  }

  // 行ドラッグ可否判定
  function canDragRow() {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }
    switch (inpQuotation.statusClassValue) {
      case QuotationStatus.DRAFT:
      case QuotationStatus.REQUESTED:
        // 顧客はECのみ可
        return auth.user().bemacFlg || !isLcm();
      default:
        return false;
    }
  }

  // 仮見積金額エリア表示判定
  function showsTempPrice() {
    // 顧客ユーザかつ未回答か再見積
    return (
      !auth.user().bemacFlg &&
      (!inpQuotation.replyDateTime || inpQuotation.reQuotationFlg)
    );
  }

  // セルの文字を赤くする判定
  function showsChangeColor(
    row: ICellRendererParams<QuotationDetailFormRowValues>
  ) {
    if (inpQuotation.statusClassValue !== QuotationStatus.ANSWERED) {
      // 回答確認中以外は適用しない
      return false;
    }
    if (inpQuotation.bemacStartFlg) {
      // BEMAC起点は適用しない
      return false;
    }

    const data = row.data!;
    const isBemacRow = data.quantity === 0;
    const hasChangedCost = data.replyUnitCost !== data.standardUnitPrice;
    const hasChangedQuantity = data.replyQuantity !== data.quantity;
    const hasChangedSubTotal = data.replyDetailSubTotal !== data.detailSubTotal;

    // 依頼と回答で値が異なるものを赤文字にする
    switch (row.colDef?.field) {
      case "replyUnitCost":
        return isBemacRow || hasChangedCost;
      case "replyQuantity":
        return isBemacRow || hasChangedQuantity;
      case "replyDetailSubTotal":
        return isBemacRow || hasChangedSubTotal;
      default:
        return isBemacRow;
    }
  }

  // 列編集可否判定
  function editableColumn(
    row: ICellRendererParams<QuotationDetailFormRowValues>
  ) {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    const data = row.data!;

    // 品目コード入力済みの場合は単位入力不可
    if (data.productCode && row.colDef?.field === "unit") {
      return false;
    }

    // 依頼数量はBEMACまたは品目コード=「---」の場合は入力不可
    if (row.colDef?.field === "quantity") {
      if (
        auth.user().bemacFlg ||
        data?.productCode === ProductCodeConst.BLANK
      ) {
        return false;
      }
    }

    // LCM、レコメンドから渡された明細は編集不可(経費明細は除く)
    // 顧客で依頼数量セルの場合は編集可
    if (data.referenceDetailNo) {
      if (
        (auth.user().bemacFlg &&
          data.productCode &&
          (data.productCode !== ProductCodeConst.SPECIAL_CODES ||
            (data.productCode === ProductCodeConst.SPECIAL_CODES &&
              row.colDef?.field !== "spec"))) ||
        (!auth.user().bemacFlg && row.colDef?.field !== "quantity")
      ) {
        return false;
      }
    }

    // LCMかつ顧客ユーザの場合は明細の編集不可
    if (isLcm() && !auth.user().bemacFlg) {
      return false;
    }

    switch (inpQuotation.statusClassValue) {
      case QuotationStatus.DRAFT:
      case QuotationStatus.REQUESTED:
        // 「作成中」「依頼中」は自社が追加した行のみ編集可
        return (
          (auth.user().bemacFlg && data.addsByBemac) ||
          (!auth.user().bemacFlg && !data.addsByBemac)
        );
      default:
        // その他は編集不可
        return false;
    }
  }

  // BEMAC回答列の編集可否判定
  function editableReplyColumn(
    rowData: ICellRendererParams<QuotationDetailFormRowValues>
  ) {
    if (readOnly) {
      // 読み取り専用モードの時は編集不可
      return false;
    }

    // LCMから渡された明細で臨注品以外は編集不可
    if (
      isLcm() &&
      rowData.data?.referenceDetailNo &&
      rowData.data?.productCode !== ProductCodeConst.SPECIAL_CODES
    ) {
      return false;
    }

    //経費行、準備費行、臨注品の場合は回答数量のみ編集不可
    if (
      (rowData.data?.productCode === ProductCodeConst.EXPENSES ||
        rowData.data?.productCode === ProductCodeConst.READY ||
        (rowData.data?.productCode === ProductCodeConst.SPECIAL_CODES &&
          rowData.data?.referenceDetailNo)) &&
      rowData.colDef?.field === "replyQuantity"
    ) {
      return false;
    }

    //品目コード=「---」の場合は編集不可
    if (rowData.data?.productCode === ProductCodeConst.BLANK) {
      return false;
    }

    // 自身がBEMACユーザで「作成中」「依頼中」は編集可
    switch (inpQuotation.statusClassValue) {
      case QuotationStatus.DRAFT:
      case QuotationStatus.REQUESTED:
        return auth.user().bemacFlg;
      default:
        return false;
    }
  }

  // グリッド内の削除ボタン使用可否
  function canDeleteRow(
    rowData: ICellRendererParams<QuotationDetailFormRowValues>
  ) {
    // 最終行または技師派遣フラグON時の経費行、準備費行の場合は削除不可
    if (
      rowData.data?.quotationDetailNo === inpDetails.length ||
      ((rowData.data?.productCode === ProductCodeConst.EXPENSES ||
        rowData.data?.productCode === ProductCodeConst.READY) &&
        inpQuotation.dispatchFlg)
    ) {
      return false;
    }
    return editableColumn(rowData);
  }

  // お客様備考注釈表示条件(作成中)
  function showsUserCommentDraft() {
    if (auth.user().bemacFlg) {
      // BEMACは非表示
      return false;
    }

    // 作成中の場合表示
    if (inpQuotation.statusClassValue === QuotationStatus.DRAFT) {
      return true;
    }
    // その他は非表示
    return false;
  }

  // お客様備考注釈表示条件(キャンセル・否認)
  function showsUserCommentDenied() {
    if (auth.user().bemacFlg) {
      // BEMACは非表示
      return false;
    }

    // 依頼中、回答確認中の場合表示
    if (
      inpQuotation.statusClassValue === QuotationStatus.REQUESTED ||
      inpQuotation.statusClassValue === QuotationStatus.ANSWERED
    ) {
      return true;
    }
    // その他は非表示
    return false;
  }

  return {
    canAddNewRow,
    showsAnswerColumns,
    isLcm,
    isGeneralEngineer,
    editableFileUpload,
    editableComment,
    canDragRow,
    showsTempPrice,
    showsChangeColor,
    editableColumn,
    editableReplyColumn,
    canDeleteRow,
    showsUserCommentDraft,
    showsUserCommentDenied,
  };
}
